/**
 * measurementUtils.js
 * ----------------------------------------------------------------------------
 * Geometria do desenho: converte uma peça normalizada (metros + posição em %)
 * em um retângulo em pixels e fornece as linhas de cota e de borda.
 *
 * IMPORTANTE: a peça guarda a posição como CENTRO em percentual do canvas
 * (compatível com o formato atual do modal). Aqui resolvemos isso em px.
 */

import { metersToPx, pxToMeters } from "./scaleUtils";
import { formatMeasure } from "@/apps/shared/utils/measurements";

/** Formata um valor em metros para exibição (2 casas). */
export { formatMeasure };

/**
 * Bounding box da peça em pixels.
 * @param {object} piece peça normalizada
 * @param {object} ctx { scale, width, height } dimensões do canvas em px
 * @returns {{x,y,w,h,cx,cy}} top-left (x,y), tamanho (w,h) e centro (cx,cy)
 */
export function getPieceBounds(piece, ctx) {
  const { scale = 1, width = 0, height = 0 } = ctx || {};

  // peças muito finas (saia, ML) recebem uma espessura visual mínima
  const visualHeightMeters =
    piece.height && piece.height > 0.1 ? piece.height : 0.1;

  const w = Math.max(metersToPx(piece.width, scale), 8);
  const h = Math.max(metersToPx(visualHeightMeters, scale), 8);

  // centro em px a partir do percentual salvo
  const cx = (piece.position.x / 100) * width;
  const cy = (piece.position.y / 100) * height;

  return {
    x: cx - w / 2,
    y: cy - h / 2,
    w,
    h,
    cx,
    cy
  };
}

/**
 * Bounding box do CONTEÚDO inteiro (todas as peças + margem para as cotas),
 * em px do desenho. Usado pelo fit-to-content (centerView).
 *
 * @param {Array} pieces peças normalizadas (só as desenháveis)
 * @param {object} ctx { scale, width, height }
 * @param {number} margin folga para linhas de cota + texto (px)
 * @returns {{x,y,w,h}|null} null quando não há peças
 */
export function getContentBounds(pieces, ctx, margin = 46) {
  const list = (pieces || []).filter(p => p && p.isStone !== false);
  if (!list.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  list.forEach(piece => {
    const b = getPieceBounds(piece, ctx);
    // swatches de acabamento ficam FORA da borda (offset 6 + size/2 + nome):
    // o enquadramento precisa incluí-los, senão saem cortados na impressão
    let extra = margin;
    const finishes = piece.edgeFinishItems || {};
    Object.keys(finishes).forEach(edge => {
      const info = finishes[edge];
      if (!info || !info.nome) return;
      const size = Math.max(16, Number(info.size) || 28);
      extra = Math.max(extra, size + 18);
    });
    minX = Math.min(minX, b.x - extra);
    minY = Math.min(minY, b.y - extra);
    maxX = Math.max(maxX, b.x + b.w + extra);
    maxY = Math.max(maxY, b.y + b.h + extra);
  });

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * CANTOS ARREDONDADOS unilaterais (peça retangular).
 * `piece.cornerRadius = { tl, tr, br, bl }` em METROS — cada canto pode ser
 * arredondado individualmente (o padrão da peça é canto RETO).
 * @returns {string|null} path SVG, ou null quando todos os cantos são retos
 */
export function getPieceCornerPath(piece, bounds) {
  const radii = piece && piece.cornerRadius;
  if (!radii || piece.shape) return null;
  const b = bounds;
  const pxPerMeterX = b.w / (piece.width || 1);
  const pxPerMeterY =
    b.h / (piece.height && piece.height > 0.1 ? piece.height : 0.1);
  const px = value => {
    const meters = Number(value) || 0;
    if (meters <= 0) return 0;
    // usa a menor escala para o arco não "achatar" em peças finas
    return Math.min(
      meters * Math.min(pxPerMeterX, pxPerMeterY),
      Math.min(b.w, b.h) / 2
    );
  };
  const tl = px(radii.tl);
  const tr = px(radii.tr);
  const br = px(radii.br);
  const bl = px(radii.bl);
  if (!tl && !tr && !br && !bl) return null;

  return [
    `M ${b.x + tl} ${b.y}`,
    `H ${b.x + b.w - tr}`,
    tr ? `A ${tr} ${tr} 0 0 1 ${b.x + b.w} ${b.y + tr}` : "",
    `V ${b.y + b.h - br}`,
    br ? `A ${br} ${br} 0 0 1 ${b.x + b.w - br} ${b.y + b.h}` : "",
    `H ${b.x + bl}`,
    bl ? `A ${bl} ${bl} 0 0 1 ${b.x} ${b.y + b.h - bl}` : "",
    `V ${b.y + tl}`,
    tl ? `A ${tl} ${tl} 0 0 1 ${b.x + tl} ${b.y}` : "",
    "Z"
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Linha (em px) de uma borda específica da peça.
 * edge ∈ 'front' | 'back' | 'left' | 'right'
 * Convenção: front = base (frente do balcão), back = topo (parede).
 */
export function getEdgeLine(piece, edge, bounds) {
  const b = bounds || getPieceBounds(piece, {});
  switch (edge) {
    case "front":
      return { x1: b.x, y1: b.y + b.h, x2: b.x + b.w, y2: b.y + b.h };
    case "back":
      return { x1: b.x, y1: b.y, x2: b.x + b.w, y2: b.y };
    case "left":
      return { x1: b.x, y1: b.y, x2: b.x, y2: b.y + b.h };
    case "right":
      return { x1: b.x + b.w, y1: b.y, x2: b.x + b.w, y2: b.y + b.h };
    default:
      return { x1: b.x, y1: b.y, x2: b.x, y2: b.y };
  }
}

/**
 * Linhas de cota no padrão técnico (ABNT/ISO), prontas para SVG.
 *
 * Cada cota possui:
 *   - extLines: linhas de chamada (extensão) que saem da peça com um pequeno
 *     gap e ultrapassam levemente a linha de cota;
 *   - dimLine: a linha de cota propriamente dita, com SETA nas duas pontas
 *     (o canvas aplica marker-start/marker-end);
 *   - label: texto centralizado com a medida.
 *
 * @param {object} piece
 * @param {object} bounds resultado de getPieceBounds
 * @param {object} opts { offset, gap } distâncias em px
 */
export function getDimensionLines(piece, bounds, opts = {}) {
  const b = bounds;
  const offset = opts.offset != null ? opts.offset : 24; // peça -> linha de cota
  const gap = opts.gap != null ? opts.gap : 4; // peça -> início da chamada
  const over = 5; // quanto a chamada passa da linha de cota
  // lados configuráveis: quando outra peça ocupa o lado padrão (balcão em L),
  // a régua flipa para o lado livre (top/left)
  const lengthSide = opts.lengthSide || "bottom";
  const widthSide = opts.widthSide || "right";

  let length = null;
  if (lengthSide === "off") {
    length = null;
  } else if (lengthSide === "top") {
    const lengthY = b.y - offset;
    length = {
      extLines: [
        { x1: b.x, y1: b.y - gap, x2: b.x, y2: lengthY - over },
        { x1: b.x + b.w, y1: b.y - gap, x2: b.x + b.w, y2: lengthY - over }
      ],
      dimLine: { x1: b.x, y1: lengthY, x2: b.x + b.w, y2: lengthY },
      label: {
        x: b.cx,
        y: lengthY - 5,
        text: `${formatMeasure(piece.width)}m`,
        rotate: 0
      }
    };
  } else {
    const lengthY = b.y + b.h + offset;
    length = {
      extLines: [
        { x1: b.x, y1: b.y + b.h + gap, x2: b.x, y2: lengthY + over },
        { x1: b.x + b.w, y1: b.y + b.h + gap, x2: b.x + b.w, y2: lengthY + over }
      ],
      dimLine: { x1: b.x, y1: lengthY, x2: b.x + b.w, y2: lengthY },
      label: {
        x: b.cx,
        y: lengthY - 5,
        text: `${formatMeasure(piece.width)}m`,
        rotate: 0
      }
    };
  }

  let width = null;
  if (widthSide === "off") {
    width = null;
  } else if (widthSide === "left") {
    const widthX = b.x - offset;
    width = {
      extLines: [
        { x1: b.x - gap, y1: b.y, x2: widthX - over, y2: b.y },
        { x1: b.x - gap, y1: b.y + b.h, x2: widthX - over, y2: b.y + b.h }
      ],
      dimLine: { x1: widthX, y1: b.y, x2: widthX, y2: b.y + b.h },
      label: {
        x: widthX - 5,
        y: b.cy,
        text: `${formatMeasure(piece.height)}m`,
        rotate: -90
      }
    };
  } else {
    const widthX = b.x + b.w + offset;
    width = {
      extLines: [
        { x1: b.x + b.w + gap, y1: b.y, x2: widthX + over, y2: b.y },
        { x1: b.x + b.w + gap, y1: b.y + b.h, x2: widthX + over, y2: b.y + b.h }
      ],
      dimLine: { x1: widthX, y1: b.y, x2: widthX, y2: b.y + b.h },
      label: {
        x: widthX - 5,
        y: b.cy,
        text: `${formatMeasure(piece.height)}m`,
        rotate: -90
      }
    };
  }

  const dims = {};
  if (length) dims.length = length;
  if (width) dims.width = width;
  return dims;
}

/**
 * SILHUETA PARAMÉTRICA (shape) — bancadas em L e U.
 *
 * `piece.shape = { kind: 'rect'|'L'|'U', params: { cutW, cutH, corner|offset } }`
 * (medidas do recorte em METROS). O financeiro NÃO muda: comprimento/largura
 * seguem sendo o retângulo envolvente (decisão de produto).
 *
 * @returns {{kind, points:[{x,y,mx,my}], polygon:string}|null} null = retângulo
 */
/**
 * Pontos do contorno da peça em METROS (sempre retorna, mesmo p/ retângulo).
 * Usado pelo desenho em planta e pela projeção isométrica.
 * @returns {Array<[mx,my]>} sentido horário a partir do canto sup-esq
 */
export function getOutlinePointsMeters(piece) {
  const W = piece.width || 0;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const shape = piece && piece.shape;
  if (!shape || shape.kind === "rect" || !W) {
    return [[0, 0], [W, 0], [W, H], [0, H]];
  }
  // formato LIVRE: recortes retangulares feitos com a BORRACHA
  if (shape.kind === "custom") {
    const notches = Array.isArray(shape.notches) ? shape.notches : [];
    if (!notches.length) return [[0, 0], [W, 0], [W, H], [0, H]];
    return traceNotchedOutline(W, H, notches);
  }
  const params = shape.params || {};
  const cutW = Math.min(Math.max(toShapeNumber(params.cutW, W * 0.4), 0.05), W - 0.05);
  const cutH = Math.min(Math.max(toShapeNumber(params.cutH, H * 0.4), 0.05), H - 0.05);

  if (shape.kind === "L") {
    const corner = params.corner || "tr";
    if (corner === "tr") {
      return [[0, 0], [W - cutW, 0], [W - cutW, cutH], [W, cutH], [W, H], [0, H]];
    }
    if (corner === "tl") {
      return [[cutW, 0], [W, 0], [W, H], [0, H], [0, cutH], [cutW, cutH]];
    }
    if (corner === "br") {
      return [[0, 0], [W, 0], [W, H - cutH], [W - cutW, H - cutH], [W - cutW, H], [0, H]];
    }
    // bl
    return [[0, 0], [W, 0], [W, H], [cutW, H], [cutW, H - cutH], [0, H - cutH]];
  }
  if (shape.kind === "U") {
    const rawOffset =
      params.offset != null ? toShapeNumber(params.offset, (W - cutW) / 2) : (W - cutW) / 2;
    const offset = Math.min(Math.max(rawOffset, 0.05), W - cutW - 0.05);
    return [
      [0, 0],
      [offset, 0],
      [offset, cutH],
      [offset + cutW, cutH],
      [offset + cutW, 0],
      [W, 0],
      [W, H],
      [0, H]
    ];
  }
  return [[0, 0], [W, 0], [W, H], [0, H]];
}

export function getPieceOutline(piece, bounds) {
  const shape = piece && piece.shape;
  if (!shape || shape.kind === "rect" || !piece.width) return null;
  const b = bounds;
  const W = piece.width;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const X = m => b.x + (m / W) * b.w;
  const Y = m => b.y + (m / H) * b.h;

  const pts = getOutlinePointsMeters(piece);
  const points = pts.map(([mx, my]) => ({ x: X(mx), y: Y(my), mx, my }));
  return {
    kind: shape.kind,
    points,
    polygon: points.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ")
  };
}

function toShapeNumber(value, fallback) {
  const n = typeof value === "number" ? value : parseFloat(`${value}`.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Contorno de um retângulo W×H MENOS recortes retangulares (borracha).
 * Estratégia robusta para geometria retilinear: grade de células a partir
 * das coordenadas dos recortes -> segmentos de fronteira -> encadeia o
 * maior anel (contorno externo) -> simplifica colineares.
 * Cada recorte deve tocar a borda (a borracha garante isso no snap).
 */
function traceNotchedOutline(W, H, notches) {
  const eps = 0.0005;
  const uniqSorted = (values, max) => {
    const sorted = values
      .map(v => Math.min(Math.max(v, 0), max))
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach(v => {
      if (!out.length || v - out[out.length - 1] > eps) out.push(v);
    });
    return out;
  };
  const xs = [0, W];
  const ys = [0, H];
  notches.forEach(n => {
    xs.push(n.x, n.x + n.w);
    ys.push(n.y, n.y + n.h);
  });
  const ux = uniqSorted(xs, W);
  const uy = uniqSorted(ys, H);
  const nx = ux.length - 1;
  const ny = uy.length - 1;
  if (nx < 1 || ny < 1) return [[0, 0], [W, 0], [W, H], [0, H]];

  // célula sólida = dentro da peça e fora de todos os recortes
  const solid = [];
  for (let i = 0; i < nx; i++) {
    solid[i] = [];
    for (let j = 0; j < ny; j++) {
      const cx = (ux[i] + ux[i + 1]) / 2;
      const cy = (uy[j] + uy[j + 1]) / 2;
      solid[i][j] = !notches.some(
        n => cx > n.x && cx < n.x + n.w && cy > n.y && cy < n.y + n.h
      );
    }
  }
  const isSolid = (i, j) => i >= 0 && j >= 0 && i < nx && j < ny && solid[i][j];

  // segmentos de fronteira (em índices de grade)
  const segs = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      if (!solid[i][j]) continue;
      if (!isSolid(i, j - 1)) segs.push([[i, j], [i + 1, j]]);
      if (!isSolid(i, j + 1)) segs.push([[i, j + 1], [i + 1, j + 1]]);
      if (!isSolid(i - 1, j)) segs.push([[i, j], [i, j + 1]]);
      if (!isSolid(i + 1, j)) segs.push([[i + 1, j], [i + 1, j + 1]]);
    }
  }
  if (!segs.length) return [[0, 0], [W, 0], [W, H], [0, H]];

  // encadeia segmentos em anéis
  const keyOf = p => `${p[0]},${p[1]}`;
  const adj = new Map();
  segs.forEach((s, si) => {
    [s[0], s[1]].forEach(p => {
      const k = keyOf(p);
      if (!adj.has(k)) adj.set(k, []);
      adj.get(k).push(si);
    });
  });
  const used = new Array(segs.length).fill(false);
  const loops = [];
  for (let s0 = 0; s0 < segs.length; s0++) {
    if (used[s0]) continue;
    used[s0] = true;
    const loop = [segs[s0][0], segs[s0][1]];
    let guard = segs.length * 2;
    while (guard-- > 0) {
      const cur = loop[loop.length - 1];
      const candidates = (adj.get(keyOf(cur)) || []).filter(si => !used[si]);
      if (!candidates.length) break;
      const si = candidates[0];
      used[si] = true;
      const seg = segs[si];
      const next = keyOf(seg[0]) === keyOf(cur) ? seg[1] : seg[0];
      if (keyOf(next) === keyOf(loop[0])) break;
      loop.push(next);
    }
    loops.push(loop);
  }

  // maior anel = contorno externo (buracos internos são ignorados)
  const shoelace = pts => {
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[(i + 1) % pts.length];
      area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area) / 2;
  };
  let best = loops[0];
  let bestArea = -1;
  loops.forEach(loop => {
    const pts = loop.map(p => [ux[p[0]], uy[p[1]]]);
    const area = shoelace(pts);
    if (area > bestArea) {
      bestArea = area;
      best = loop;
    }
  });
  let pts = best.map(p => [ux[p[0]], uy[p[1]]]);

  // remove pontos colineares consecutivos
  const simplified = [];
  for (let i = 0; i < pts.length; i++) {
    const prev = pts[(i - 1 + pts.length) % pts.length];
    const cur = pts[i];
    const next = pts[(i + 1) % pts.length];
    const collinear =
      (Math.abs(prev[0] - cur[0]) < eps && Math.abs(cur[0] - next[0]) < eps) ||
      (Math.abs(prev[1] - cur[1]) < eps && Math.abs(cur[1] - next[1]) < eps);
    if (!collinear) simplified.push(cur);
  }
  return simplified.length >= 4 ? simplified : pts;
}

/**
 * Segmentos (px) que compõem uma borda lógica (front/back/left/right) da
 * peça, respeitando a silhueta. Retângulo → 1 segmento (getEdgeLine).
 * Trechos internos do L/U (verticais fora das laterais) contam como "back".
 */
export function getEdgeSegments(piece, edge, bounds) {
  const outline = getPieceOutline(piece, bounds);
  if (!outline) return [getEdgeLine(piece, edge, bounds)];
  const W = piece.width;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const eps = 0.0001;
  const segments = [];
  const pts = outline.points;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const c = pts[(i + 1) % pts.length];
    const horizontal = Math.abs(a.my - c.my) < eps;
    let side;
    if (horizontal) {
      if (Math.abs(a.my - H) < eps) side = "front";
      else if (Math.abs(a.my) < eps) side = "back";
      // horizontais internas (recortes): lado mais próximo
      else side = a.my > H / 2 ? "front" : "back";
    } else if (Math.abs(a.mx) < eps) {
      side = "left";
    } else if (Math.abs(a.mx - W) < eps) {
      side = "right";
    } else {
      // verticais internas (recortes): lado mais próximo
      side = a.mx > W / 2 ? "right" : "left";
    }
    if (side === edge) {
      segments.push({ x1: a.x, y1: a.y, x2: c.x, y2: c.y });
    }
  }
  return segments.length ? segments : [getEdgeLine(piece, edge, bounds)];
}

/**
 * Cotas extras da silhueta (largura/profundidade do recorte do L/U),
 * desenhadas junto ao recorte. Mesmo formato das demais cotas.
 */
export function getShapeDimensions(piece, bounds) {
  const outline = getPieceOutline(piece, bounds);
  if (!outline) return [];
  const shape = piece.shape || {};
  const params = shape.params || {};

  // recortes da BORRACHA: cota de largura/altura dentro do vão apagado
  if (outline.kind === "custom") {
    const W = piece.width;
    const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
    const b = bounds;
    const X = m => b.x + (m / W) * b.w;
    const Y = m => b.y + (m / H) * b.h;
    const dims = [];
    (shape.notches || []).forEach(n => {
      const midY = Y(n.y + n.h / 2);
      const midX = X(n.x + n.w / 2);
      dims.push({
        extLines: [],
        dimLine: { x1: X(n.x), y1: midY, x2: X(n.x + n.w), y2: midY },
        label: {
          x: midX,
          y: midY - 4,
          text: `${formatMeasure(n.w)}m`,
          rotate: 0
        }
      });
      dims.push({
        extLines: [],
        dimLine: { x1: midX, y1: Y(n.y), x2: midX, y2: Y(n.y + n.h) },
        label: {
          x: midX - 5,
          y: midY,
          text: `${formatMeasure(n.h)}m`,
          rotate: -90
        }
      });
    });
    return dims;
  }
  const W = piece.width;
  const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
  const cutW = Math.min(Math.max(toShapeNumber(params.cutW, W * 0.4), 0.05), W - 0.05);
  const cutH = Math.min(Math.max(toShapeNumber(params.cutH, H * 0.4), 0.05), H - 0.05);
  const b = bounds;
  const X = m => b.x + (m / W) * b.w;
  const Y = m => b.y + (m / H) * b.h;
  const dims = [];

  if (outline.kind === "L") {
    const corner = params.corner || "tr";
    const top = corner === "tr" || corner === "tl";
    const right = corner === "tr" || corner === "br";
    const x1 = right ? X(W - cutW) : X(0);
    const x2 = right ? X(W) : X(cutW);
    const yLine = top ? Y(0) - 10 : Y(H) + 10;
    dims.push({
      extLines: [],
      dimLine: { x1, y1: yLine, x2, y2: yLine },
      label: { x: (x1 + x2) / 2, y: yLine - 3, text: `${formatMeasure(cutW)}m`, rotate: 0 }
    });
    const xLine = right ? X(W) + 10 : X(0) - 10;
    const y1 = top ? Y(0) : Y(H - cutH);
    const y2 = top ? Y(cutH) : Y(H);
    dims.push({
      extLines: [],
      dimLine: { x1: xLine, y1, x2: xLine, y2 },
      label: { x: xLine + (right ? 8 : -8), y: (y1 + y2) / 2, text: `${formatMeasure(cutH)}m`, rotate: -90 }
    });
  } else if (outline.kind === "U") {
    const rawOffset = params.offset != null ? toShapeNumber(params.offset, (W - cutW) / 2) : (W - cutW) / 2;
    const offset = Math.min(Math.max(rawOffset, 0.05), W - cutW - 0.05);
    const yLine = Y(cutH) + 12;
    dims.push({
      extLines: [],
      dimLine: { x1: X(offset), y1: yLine, x2: X(offset + cutW), y2: yLine },
      label: {
        x: X(offset + cutW / 2),
        y: yLine - 3,
        text: `${formatMeasure(cutW)}m`,
        rotate: 0
      }
    });
  }
  return dims;
}

/**
 * Linha de eixo (traço-ponto) em cruz, passando pelo centro e ultrapassando o
 * raio — usada em furos / recortes circulares.
 * @returns {{ h, v, dash }} segmentos horizontal/vertical + padrão dash-dot
 */
export function getCenterLines(cx, cy, r, overshoot = 7) {
  const len = r + overshoot;
  return {
    h: { x1: cx - len, y1: cy, x2: cx + len, y2: cy },
    v: { x1: cx, y1: cy - len, x2: cx, y2: cy + len },
    dash: "6 2 1 2" // traço-ponto
  };
}

/**
 * Cota de diâmetro (Ø) com linha de chamada saindo na diagonal do furo.
 * O valor é convertido de px -> medida real (cm) usando a escala do projeto,
 * já que o desenho compartilha o mesmo espaço métrico das peças.
 * @param {number} diameterPx diâmetro em px do desenho
 * @param {number} scale escala do projeto
 * @returns {{ leader, label }}
 */
export function getDiameterCallout(cx, cy, r, diameterPx, scale = 1) {
  const k = Math.SQRT1_2; // cos/sin 45°
  const start = { x: cx + r * k, y: cy - r * k }; // ponto na borda (diagonal sup. dir.)
  const end = { x: cx + r + 22, y: cy - r - 22 }; // ponta da chamada
  const cm = pxToMeters(diameterPx, scale) * 100; // px -> m -> cm
  return {
    leader: { x1: start.x, y1: start.y, x2: end.x, y2: end.y },
    label: {
      x: end.x + 2,
      y: end.y - 2,
      text: `Ø${cm.toFixed(cm >= 10 ? 0 : 1)}cm`
    }
  };
}

/**
 * Converte um deslocamento do mouse (coordenadas globais do canvas) para o
 * sistema local de uma peça rotacionada. Elementos filhos do <g> rotacionado
 * precisam do delta inverso para acompanhar o cursor na mesma direção.
 */
export function globalDeltaToPieceLocal(dx, dy, rotation = 0) {
  const radians = ((Number(rotation) || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    dx: dx * cos + dy * sin,
    dy: -dx * sin + dy * cos
  };
}
